<template>
  <div
    ref="containerEl"
    class="titled-document command-anchor"
    :class="{ 'embedded-mode': embeddedMode, wide: isWideDocument }"
  >
    <div class="title-wrapper">
      <!-- Compact context row right above the title (e.g. a task's capture
           context); `:empty` hides it when the slot renders nothing. -->
      <div v-if="$slots.aboveTitle" class="above-title">
        <slot name="aboveTitle" />
      </div>
      <h1
        class="title"
        :class="{
          isTitleGrayedOut,
          hasPreTitle: !!$slots.preTitle,
          hasPostTitle: !!$slots.postTitle && !!value.title,
        }"
        @click.self="() => focusTitle()"
      >
        <slot name="preTitle" />
        <RInput v-if="loadingTitle" placeholder="Loading..." :disabled="true" />
        <!-- Common block for the input and the post-title button: with a
             non-empty title both lay out as inline content, so the button
             flows at the end of the last line when the title wraps. -->
        <div v-else class="title-main" @click.self="() => focusTitle()">
          <RInput
            ref="titleEl"
            v-model:value="value.title"
            class="title-input"
            placeholder="Untitled"
            :debounce="debounceTitle"
            :disabled="!editableTitle"
            @exit-down="focusEditor"
            @exit-right="focusEditor"
            @enter="inputSpill"
          />
          <div v-if="$slots.postTitle" class="post-title">
            <slot name="postTitle" />
          </div>
        </div>
      </h1>
      <div v-if="$slots.properties" class="properties">
        <slot name="properties" />
      </div>
      <slot name="subTitle" />
    </div>

    <div
      v-if="!loadingNotes && empty"
      class="empty-notes-dropzone"
      :class="{ 'drag-over': isDragOver }"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <div
        class="empty-notes-dropzone__content"
        :class="{ hidden: isDragOver }"
      >
        <TemplateSelector
          v-if="templates"
          :templates="templates"
          @initialize="initialize"
          @choose-template="chooseTemplate"
        />
        <div v-else class="templates">
          <p class="empty" @click="initialize">
            Press <span>{{ formatHotkey(keyEnter) }}</span> to write notes.
          </p>
        </div>
      </div>
      <div v-if="isDragOver" class="drop-message">
        <FileIcon class="drop-message__icon" />
        <span>Drop the file to add it to the notes</span>
      </div>
    </div>
    <template v-else-if="document && personalWorkspaceId && !loadingNotes">
      <Suspense>
        <Editor
          ref="editorEl"
          :notes="document"
          :workspace-id="personalWorkspaceId"
          rect-container-selector=".titled-document"
          rect-ignore-selector=".title-wrapper"
          @change="onChange"
        />
        <template #fallback>
          <Loader>
            <PageLoading :with-title="false" class="loading" />
          </Loader>
        </template>
      </Suspense>
    </template>
    <Loader v-else>
      <PageLoading class="loading" />
    </Loader>
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import { Document } from "@ctrl/controller-rpc";

import assert from "@/assert";
import { createBlockParagraph } from "@/components/document";
import { proseMirrorDocToDocument } from "@/components/editor/document-to-prosemirror";
import Editor from "@/components/editor/Editor.vue";
import FileIcon from "@/components/icons/FileIcon.vue";
import Loader from "@/components/Loader.vue";
import allTemplates, {
  instantiate as instantiateTemplate,
  TemplateName,
} from "@/components/templates";
import { useDocumentMerge } from "@/composables/useDocumentMerge";
import { useFileDropZone } from "@/composables/useFileDropZone";
import { useScrollToBlockFromHash } from "@/composables/useScrollToBlockFromHash";
import {
  deletionTargetKey,
  executeDeletion,
  mergeDeletionPlans,
  ownedEntityTargets,
  planDeletions,
  removedEntityTargets,
  type DeletionContext,
  type DeletionPlan,
} from "@/utils/deletion";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";

import * as inject from "./inject";
import PageLoading from "./PageLoading.vue";
import RInput from "./RInput.vue";
import TemplateSelector from "./TemplateSelector.vue";
import { addOrRemoveListenerOnLink, getHTMLElement } from "./utils";

type DocumentType = "task" | "event" | "page";

// Minimal surface of the async `Editor` instance we interact with via the ref.
// Includes `whenReady`/`isReady` so it is compatible with `EditorReadyLike`
// expected by `useScrollToBlockFromHash`.
type EditorInstance = {
  focusEditorStart?: () => void;
  focusEditorEnd?: () => void;
  focus?: () => void;
  selectAll?: () => void;
  focused?: boolean;
  focusBlock?: (id: string) => void;
  insertAttachments?: (files: File[]) => void;
  whenReady?: () => Promise<void>;
  isReady?: () => boolean;
};

export type Value = {
  notes?: Document;
  title: string;
  type?: DocumentType;
};

export default defineComponent({
  components: {
    Loader,
    RInput,
    PageLoading,
    Editor,
    TemplateSelector,
    FileIcon,
  },
  props: {
    debounce: {
      type: Number,
      default: 5000,
    },
    debounceTitle: {
      type: Number,
      default: 5000,
    },
    editableTitle: {
      type: Boolean,
      default: true,
    },
    loadingTitle: {
      type: Boolean,
      default: false,
    },
    loadingNotes: {
      type: Boolean,
      default: false,
    },
    templates: {
      type: Array as PropType<Array<TemplateName> | null>,
      default: null,
    },
    value: {
      type: Object as PropType<Value>,
      required: true,
    },
    task: {
      type: Boolean,
      default: false,
    },
    isTitleGrayedOut: {
      type: Boolean,
      default: false,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
    autoInitialize: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const isWideDocument = inject.isWideDocument();
    const baseLogger = inject.logger();
    const logger = baseLogger.forModule("TitledDocument");
    const {
      autoFocus,
      autoInitialize,
      debounce: debounceDelay,
      editableTitle,
      value,
    } = toRefs(props);

    // ⚠ Is this right? It *looks* like this component is only used for stuff in the personal workspace (e.g., notes, people, events)
    // If not, we need to rethink how to find the current workspace, or take a required prop to let the call sites decide
    const profile = inject.profile();
    const personalWorkspaceId = computed(
      () => profile.value.user.personal.workspaceId || undefined
    );

    // Template references
    const editorEl = ref<EditorInstance | null>(null);
    const containerEl = ref<HTMLElement | null>(null);
    const titleEl = ref<InstanceType<typeof RInput>>();
    const processTitleLinks = (action: "add" | "remove") => {
      if (titleEl.value) {
        const el = getHTMLElement(titleEl.value);
        if (el) addOrRemoveListenerOnLink(action, el, controller, router);
      }
    };
    onBeforeUnmount(() => processTitleLinks("remove"));
    watch(
      () => [value.value.title, titleEl.value],
      () => {
        nextTick(() => processTitleLinks("add"));
      }
    );
    // Content - using useDocumentMerge composable for merge logic. Absent
    // notes pass through as undefined: useDocumentMerge treats it as "not
    // loaded yet" and adopts the first real document without a merge.
    const notes = computed(() => value.value.notes);

    // Entities whose blocks the edits since the last push removed (todo,
    // recurrent-task and Object blocks), each cascade planned at the edit —
    // while the host's notes still hold the block and the controller can
    // still answer for the target — and run once the removal is pushed:
    // after the push, deleting a native the controller already took is a
    // no-op, but its nested pages and custom objects (which the controller
    // never chases) are still in the plan. Keyed by `deletionTargetKey`, so
    // a block restored before the push (undo) voids its entity's deletion.
    const pendingRemovals = new Map<string, Promise<DeletionPlan>>();
    const deletionContext = (): DeletionContext => ({
      controller,
      baseLogger,
      profile,
      workspaceId: personalWorkspaceId.value,
    });
    const executePendingRemovals = (pushed: Document) => {
      if (pendingRemovals.size === 0) return;
      const present = new Set(
        ownedEntityTargets(pushed.blocks, baseLogger).map((target) =>
          deletionTargetKey(target, personalWorkspaceId.value)
        )
      );
      const plans: Promise<DeletionPlan>[] = [];
      for (const [key, plan] of pendingRemovals) {
        if (!present.has(key)) plans.push(plan);
      }
      pendingRemovals.clear();
      if (plans.length === 0) return;
      const context = deletionContext();
      void Promise.all(plans)
        .then((plans) =>
          executeDeletion(context, mergeDeletionPlans(plans), {
            skipParentNotesCleanup: true,
          })
        )
        .catch((error) => {
          logger.error("deleting the removed blocks' entities failed", {
            error,
          });
        });
    };

    const {
      localDocument: document,
      handleLocalChange,
      flush,
      empty,
      release: releaseMerge,
    } = useDocumentMerge(
      notes,
      controller,
      baseLogger,
      (doc) => {
        value.value.notes = doc;
        executePendingRemovals(doc);
      },
      {
        debounceDelay: debounceDelay.value,
        label: `TitledDocument/${value.value.type ?? "document"}`,
      }
    );

    const displayEditor = computed(
      () =>
        !!document.value &&
        !!personalWorkspaceId.value &&
        !props.loadingNotes &&
        !empty.value
    );

    // Register flush callback for Cmd+S to trigger immediate save
    const pendingChangeFlushes = inject.pendingChangeFlushes();
    onMounted(() => pendingChangeFlushes.add(flush));
    onBeforeUnmount(() => {
      pendingChangeFlushes.delete(flush);
      // Push any pending debounced edit before the editor goes away (same as
      // GraphNotes). Route navigations are already flushed earlier by
      // useObject's guards; this covers teardowns without one, e.g. a `:key`
      // remount, where the parent persisting `value.notes` stays alive.
      flush();
      releaseMerge();
    });

    const flushTitle = () => titleEl.value?.flush();
    const runWhenEditorReady = async (
      action: (editorInstance: EditorInstance) => void | Promise<void>
    ) => {
      if (editorEl.value) {
        await action(editorEl.value);
        return;
      }

      await new Promise<void>((resolve) => {
        const stopWatch = watch(
          editorEl,
          (newValue) => {
            if (newValue) {
              stopWatch();
              resolve();
            }
          },
          { immediate: true }
        );
      });

      if (editorEl.value) {
        await action(editorEl.value);
      }
    };

    const { release: releaseScrollToBlock } = useScrollToBlockFromHash({
      runWhenEditorReady,
      enabled: displayEditor,
      getContainerEl: () => containerEl.value,
    });
    onBeforeUnmount(releaseScrollToBlock);

    // Caret
    const focusEditor = async () => {
      await runWhenEditorReady(async (editorInstance) => {
        await nextTick();
        editorInstance.focusEditorStart?.();
      });
    };
    const focusEditorEnd = async () => {
      await runWhenEditorReady(async (editorInstance) => {
        await nextTick();
        editorInstance.focusEditorEnd?.();
      });
    };
    // Edition
    const onConcatTitle = (text: string) => {
      const index = value.value.title.length;
      if (text) {
        value.value.title += text;
      }
      nextTick(() => {
        titleEl.value?.focus(index);
      });
    };
    const inputSpill = (text: string, caretIndex: number | null) => {
      if (!text) return;
      const content = text.slice(caretIndex || 0);
      value.value.title = text.slice(0, caretIndex || 0);
      assert(document.value);
      document.value.blocks.splice(0, 0, createBlockParagraph(content));
      nextTick(() => editorEl.value?.focusEditorStart?.());
    };
    const onToggle = (index: number) => {
      assert(document.value);
      const block = document.value.blocks[index];
      assert(block.type === "heading");
      block.retracted = !block.retracted;
    };
    // Templates
    const chooseTemplate = async (name: TemplateName) => {
      assert(document.value);
      assert(document.value.blocks.length == 0);
      const template = allTemplates[name];
      document.value.blocks = instantiateTemplate(template);
      if (!titleEl.value?.inputEl?.innerHTML && name !== "empty")
        value.value.title = template.title;
      if (name !== "empty")
        nextTick(() => {
          window?.getSelection()?.removeAllRanges();
        });
      assert(document.value?.blocks);
      await nextTick();
      flush();
      await nextTick();
      await focusEditor();
    };
    const focusEmptyTitle = (start: boolean = false) => {
      if (titleEl.value && !titleEl.value.value) focusTitle(start);
    };
    const focusBlock = async (id: string) => {
      await runWhenEditorReady(async (editorInstance) => {
        await nextTick();
        editorInstance.focusBlock?.(id);
      });
    };

    const focusTitle = (start: boolean = false) => {
      if (editableTitle.value) nextTick(() => titleEl.value?.focus(start));
    };
    const onExitLeft = () => focusTitle();
    const onExitUp = () => focusTitle(true);
    const initialize = () => {
      assert(document.value);
      assert(document.value.blocks.length == 0);
      document.value.blocks = [createBlockParagraph()];
      nextTick(() => {
        flush();
        editorEl.value?.focus?.();
      });
    };

    // Dropping a file onto the empty state initializes the notes and inserts
    // the file, since there is no editor to receive the drop otherwise.
    const onEmptyNotesFilesDropped = async (files: File[]) => {
      if (empty.value) initialize();
      await runWhenEditorReady((editorInstance) =>
        editorInstance.insertAttachments?.(files)
      );
    };
    const { isDragOver, onDragEnter, onDragOver, onDragLeave, onDrop } =
      useFileDropZone(onEmptyNotesFilesDropped);

    // Auto-initialize if requested and document is empty
    watch(
      [empty, autoInitialize],
      ([isEmpty, shouldAutoInit]) => {
        if (isEmpty && shouldAutoInit) {
          initialize();
        }
      },
      { immediate: true }
    );

    // Auto-focus if requested after document is not empty
    watch([empty, autoFocus], ([isEmpty, shouldAutoFocus]) => {
      if (!isEmpty && shouldAutoFocus) {
        nextTick(() => editorEl.value?.focus?.());
      }
    });

    const onChange = (
      proseMirrorDoc: ProseMirrorNode,
      shouldFlush?: boolean
    ) => {
      const newDocument = proseMirrorDocToDocument(proseMirrorDoc);
      if (document.value) {
        const workspaceId = personalWorkspaceId.value;
        const removed = removedEntityTargets(
          document.value.blocks,
          newDocument.blocks,
          baseLogger,
          workspaceId
        );
        if (removed.length > 0) {
          const context = deletionContext();
          // One plan per target, so a target restored before the push can
          // be voided alone; the plans are merged when run.
          for (const target of removed)
            pendingRemovals.set(
              deletionTargetKey(target, workspaceId),
              planDeletions(context, [target])
            );
        }
      }
      handleLocalChange(newDocument, shouldFlush);
    };

    const selectAll = () => {
      editorEl.value?.selectAll?.();
    };

    const hasBlockSelected = () => editorEl.value?.focused;

    const hasBlockFocused = () => editorEl.value?.focused;

    return {
      allTemplates,
      chooseTemplate,
      containerEl,
      document,
      editorEl,
      empty,
      flush,
      flushTitle,
      focusBlock,
      focusTitle,
      focusEditor,
      focusEditorEnd,
      focusEmptyTitle,
      formatHotkey: hotkeys.format.stringify,
      hasBlockFocused,
      hasBlockSelected,
      initialize,
      inputSpill,
      isDragOver,
      isWideDocument,
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      keyEnter: keymap.keyEnter,
      keysUpOrDown: keymap.keysUpOrDown,
      onChange,
      onConcatTitle,
      onExitLeft,
      onExitUp,
      onToggle,
      selectAll,
      titleEl,
      personalWorkspaceId,
    };
  },
});
</script>

<style lang="scss" scoped>
.titled-document {
  min-height: 100%;
  width: calc(100% - 192px);
  padding: 0 96px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  height: fit-content;
  /* For the rectangle selection in the new editor */
  position: relative;

  &.embedded-mode {
    width: 100%;
    padding: 0;

    .title-wrapper {
      padding-top: 0;
    }
  }

  &.wide {
    .title-wrapper,
    .templates,
    .empty-notes-dropzone,
    .loading {
      max-width: $document-wide-max-width;
    }

    :deep(.editor) {
      max-width: $document-wide-max-width;
    }
  }

  .title-wrapper,
  .templates,
  .empty-notes-dropzone {
    width: 100%;
    max-width: $document-max-width;
  }

  .empty-notes-dropzone {
    // Extend the highlight outward from the text (negative margins offset the
    // padding so the content keeps its position).
    padding: 12px 16px;
    margin: -12px -16px;
    @include empty-notes-dropzone;
  }

  .drop-message {
    @include drop-message;
  }

  .editor {
    width: 100%;
  }

  :deep(.drop-list) {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  .title-wrapper {
    margin-bottom: 24px;
    padding-top: 30px;

    .above-title {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 8px;
      // The button's hover pill already carries 4px of padding below the
      // icon; keep the extra breathing room small.
      margin-bottom: 4px;

      // The slot may render nothing (e.g. a task without context); hide the
      // wrapper so it does not add a stray margin above the title.
      &:empty {
        display: none;
      }
    }

    .properties {
      margin-top: 16px;
    }

    .title {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      position: relative;
      display: flex;
      gap: 15px;
      // The area right of a shrunk title still focuses it (see @click.self).
      cursor: text;

      .title-main {
        flex: 1;
        min-width: 0;
        cursor: text;

        // Empty title: keep the placeholder and its click area full-width,
        // the post-title button beside them.
        display: flex;
        gap: 15px;

        .title-input {
          flex: 1;
          min-width: 0;
        }

        .post-title {
          display: flex;
          align-items: center;
          // Centering on the line box sits slightly high relative to the
          // text glyphs; nudge down to match the pre-title checkbox
          // (`top: 13px` on `.checkbox` in Task.vue).
          position: relative;
          top: 3px;

          // The slot may render nothing (e.g. a task without context); hide
          // the wrapper so it does not add a stray flex gap.
          &:empty {
            display: none;
          }
        }
      }

      // Non-empty title with a post-title button: lay the input out as
      // inline content so the button flows right after the text — at the
      // end of the last line when the title wraps.
      &.hasPostTitle .title-main {
        display: block;

        .title-input,
        .title-input :deep(.input) {
          display: inline;
        }

        // The contenteditable keeps a trailing <br> as a caret helper;
        // inline it would push the button onto its own line.
        .title-input :deep(.input br:last-child) {
          display: none;
        }

        .post-title {
          display: inline-flex;
          // `middle` is relative to the parent's font: here `.title-main`
          // inherits the h1 size, so half its x-height already centers the
          // icon on the lowercase body — no extra nudge (unlike the object
          // page, whose title wrapper keeps the body font and needs -5px).
          vertical-align: middle;
          top: 0;
          margin-inline-start: 8px;

          &:empty {
            display: none;
          }
        }
      }
    }

    .title,
    .title input,
    .title textarea {
      @extend %font-h1;
      width: 100%;
      @include themed() {
        color: t($primary);
      }
      &.isTitleGrayedOut {
        @include themed() {
          color: t($grey-60);
        }
      }
    }

    .title .loader {
      width: 100%;
      @include themed() {
        color: t($grey-50);
      }
    }
  }

  ::placeholder {
    @include themed() {
      color: t($grey-30);
    }
  }

  .content {
    flex-grow: 1;
  }

  .empty {
    @include themed() {
      color: t($grey-70);
    }

    .lock {
      margin: 0 7px -2px 2px;
    }
  }

  .templates {
    p.empty {
      margin-top: 0;
      @extend %font-body-regular;
      margin-bottom: 12px;
      @include themed() {
        color: t($grey-40);
      }

      span {
        padding: 3px 6px;
        border-radius: 2px;
        @extend %font-caption-regular;
        @include themed() {
          border: 1px solid t($grey-15);
        }
      }
    }
  }

  .loading {
    max-width: $document-max-width;
    margin: auto;
  }
}
</style>
